import { Typography } from '@mui/material'
import colors from 'styles/colors'
import { useTranslation } from 'react-i18next'
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined'
import { CardTag } from 'components/molecules/home/PathCardMolecules'
import { IconContainer } from 'components/atoms/Icons'
import PsychologyOutlinedIcon from '@mui/icons-material/PsychologyOutlined'
import { AssesmentButton } from 'components/molecules/home/PathCardActions'
import { ColStack } from 'components/atoms/container/InCardStack'
import RowStack from 'components/atoms/container/RowStack'
import { formatDate } from 'utils/formatDateTime'
import LinearProgressBar from 'components/atoms/LinearProgressBar'
import BoxWithBackground from 'components/atoms/container/BoxWithBackground'
import { Tag } from 'components/atoms/Text'
import { FacilitatedSessionsDisplay } from 'components/molecules/adminDashboard/cohorts/FacilitatedSessions'
import moment from 'moment'

export const EyebrowText = ({ children, ...props }) => (
  <Typography
    variant='eyebrow2'
    fontWeight={400}
    color={colors.MidnightTeal300}
    {...props}>
    {children}
  </Typography>
)

export const CurrentPathProgress = ({ goal, leaderboardShown }) => {
  const { t } = useTranslation()
  const totalMilestones = goal.l1Steps?.length - 1
  const progressPercentage = goal.completed
    ? 100
    : goal.stats
      ? Math.floor(
          (goal.stats?.numberOfCompletedL1Steps / totalMilestones) * 100
        )
      : 0
  return (
    <ColStack
      spacing={12}
      flex={1}
      maxWidth={leaderboardShown ? 350 : undefined}
      minWidth={leaderboardShown ? undefined : 280}>
      <CardTag
        sx={{
          backgroundColor: colors.MetalGrey75
        }}
        component={'div'}
        showEllipsis={false}>
        <RowStack spacing={1}>
          <IconContainer icon={<PsychologyOutlinedIcon />} size={16} />
          <Typography variant='body1Small' fontWeight={500}>
            {goal.leadershipModelName}
          </Typography>
        </RowStack>
      </CardTag>

      <ColStack spacing={0}>
        <EyebrowText>{t('pathsAndModels.path')}</EyebrowText>
        <Typography variant='h2'>{goal.name}</Typography>
        {goal.completed && (
          <Typography>
            {t('common.completed')} {formatDate(goal.progress?.completedAt)}
          </Typography>
        )}
      </ColStack>

      {!goal.completed && !goal.opensAt && (
        <ColStack spacing={4}>
          <LinearProgressBar
            value={progressPercentage}
            showProgressIcon={true}
            barSx={{
              height: 20,
              width: '100%',
              borderRadius: 12.5,
              backgroundColor: colors.MutedTeal200,
              '& .MuiLinearProgress-bar': {
                borderRadius: 25,
                backgroundColor: colors.MidnightTeal400
              }
            }}
          />

          <EyebrowText textTransform={'none'}>
            {t('home.overallNofNMilestones', {
              completed: goal.completed
                ? totalMilestones
                : goal.stats?.numberOfCompletedL1Steps || 0,
              total: totalMilestones
            })}
          </EyebrowText>

          {goal.progress?.started && (
            <EyebrowText textTransform={'none'}>
              {t('home.started')} {formatDate(goal.progress?.started)}
            </EyebrowText>
          )}
        </ColStack>
      )}

      <AssesmentButton
        goal={goal}
        variant={'contained'}
        startIcon={<FactCheckOutlinedIcon />}
        btnSize={'small'}>
        {t('home.yourAssessments')}
      </AssesmentButton>
    </ColStack>
  )
}

export const NextSession = ({ goal, currentL1Milestone }) => {
  const { t } = useTranslation()
  const isOverDue = currentL1Milestone?.cohortSession?.facilitationDate
    ? moment()
        .startOf('day')
        .isAfter(
          moment(currentL1Milestone?.cohortSession?.facilitationDate).startOf(
            'day'
          )
        )
    : false

  return (
    <>
      {currentL1Milestone && (
        <>
          {currentL1Milestone?.cohortSession ? (
            <FacilitatedSessionsDisplay
              cohortSessionId={goal?.cohortSession?.id}
              goalId={goal?.id}>
              <BoxWithBackground
                sx={{
                  backgroundColor: isOverDue
                    ? colors.Peach50
                    : colors.MetalGrey75,
                  border: isOverDue ? `1px solid ${colors.Peach400}` : 'none',
                  borderRadius: 2,
                  width: '100%',
                  padding: '4px 8px 4px 6px'
                }}>
                <RowStack justifyContent={'space-between'}>
                  <RowStack spacing={4}>
                    <Tag
                      variant={'eyebrow'}
                      sx={{
                        paddingX: 1,
                        padding: 2,
                        backgroundColor: isOverDue
                          ? colors.Peach100
                          : colors.Grey200,
                        color: isOverDue
                          ? colors.Watermelon400
                          : colors.MidnightTeal400,
                        fontSize: 11,
                        fontWeight: 700,
                        lineHeight: '110%'
                      }}>
                      {isOverDue
                        ? `${t('pathsAndModels.overdue')}!`
                        : t('pathsAndModels.nextSession')}
                    </Tag>

                    <Typography
                      variant='body1Small'
                      color={
                        isOverDue
                          ? colors.Watermelon400
                          : colors.MidnightTeal400
                      }>
                      {isOverDue && `${t('pathsAndModels.sessionClosed')} `}
                      {formatDate(
                        currentL1Milestone?.cohortSession?.facilitationDate
                      )}
                    </Typography>
                  </RowStack>
                </RowStack>
              </BoxWithBackground>
            </FacilitatedSessionsDisplay>
          ) : (
            <ColStack spacing={0}>
              <EyebrowText>{t('home.milestone')}</EyebrowText>
              <Typography variant='h4'>
                {goal.stats.currentL1Milestone?.name}
              </Typography>
            </ColStack>
          )}
        </>
      )}
    </>
  )
}
